import { Box, Button, Stack, Theme, Typography } from '@mui/material';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router';

import { Page } from '@/components/Layout/Page/Page';
import { OnboardingGuide } from '@/components/OnboardingGuide';

import { ROUTE } from '@/constants/routes';

import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { useApplicationStatuses } from '@/hooks/useApplicationStatuses';
import { useTranslate } from '@/hooks/useTranslate';

import { signOut } from '@/redux/auth/actions';
import { selectLoggedInUser } from '@/redux/auth/selectors';

import { getDataTestId } from '@/utils/browser';
import { PROGRESS_STEPS } from '@/utils/progressSteps';

import {
  AdultContactForm,
  AdultContactFormType,
} from '../components/ApplicationTypeSelection/AdultContactForm';

const AddAdultContactPage: React.FC = () => {
  const { t, language } = useTranslate();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectLoggedInUser);
  const [adultContact, setAdultContact] = useState<AdultContactFormType>();
  const { isJointAdultAgeRestriction } = useApplicationStatuses();

  const handleBackClick = () => {
    if (isJointAdultAgeRestriction) {
      dispatch(signOut());
    } else {
      navigate(ROUTE.ACCOUNT_TYPE);
    }
  };

  const handleSubmitSuccess = (data: AdultContactFormType) => {
    setAdultContact(data);
  };

  const adultEmail = adultContact?.email || user?.adultEmail;

  return (
    <Page
      paperProps={{
        color: (theme: Theme) => theme.palette.neutral[1000],
        position: 'right',
        size: 'big',
        children: <OnboardingGuide />,
      }}
      progressProps={PROGRESS_STEPS.thirdPartyComplianceCheckStep}
      hasLogo
    >
      <Stack direction="column" spacing={4} flex={1}>
        {adultEmail ? (
          <>
            <Typography variant="h3" textAlign="center" mb={1}>
              {t('page.addAdultContact.result.title')}
            </Typography>
            <Typography
              variant="body2"
              textAlign="center"
              mb={2}
              maxWidth={320}
            >
              <Trans
                i18nKey="page.addAdultContact.result.content"
                language={language}
                as="div"
                values={{
                  adultEmail,
                }}
                components={{
                  strong: <strong />,
                }}
              />
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h3" textAlign="center" mb={1}>
              {t('page.addAdultContact.title')}
            </Typography>
            <Box maxWidth={360} width="100%">
              <AdultContactForm onSubmitted={handleSubmitSuccess} />
            </Box>
          </>
        )}
      </Stack>
      <Button
        onClick={handleBackClick}
        variant="text"
        {...getDataTestId(
          isJointAdultAgeRestriction
            ? 'back-to-login'
            : 'go-back-to-application',
        )}
      >
        {isJointAdultAgeRestriction
          ? t('page.addAdultContact.button.backToLogin')
          : t('common.button.backToApplication')}
      </Button>
    </Page>
  );
};

export default AddAdultContactPage;
